import Vue from 'vue'
import mutations from './mutations'
import companies from '@/api/companies'
import { arrayToObject, objectToArray } from '@/utils/object'
import { notify } from '@/utils/error'

export default {
  namespaced: true,

  state: {
    myCompany: null,
    childCompanies: {},
    invoiceConsistency: null
  },

  getters: {
    clients (state) {
      const companies = objectToArray(state.childCompanies)
      return companies.filter(company => company.can_add_company === false)
    },
    distributors (state) {
      const companies = objectToArray(state.childCompanies)
      return companies.filter(company => company.can_add_company === true)
    },
    isDealer (state) {
      if (state.myCompany && state.myCompany.is_top_dealer) return true
      if (state.myCompany && state.myCompany.company_type === 2) return true
      return false
    }
  },

  actions: {
    getElectronicDeviceListForDealer ({ rootState }, { startDate, endDate }) {
      return new Promise((resolve, reject) => {
        companies.getElectronicDeviceListForDealer({ startDate, endDate })
          .then(response => {
            resolve(response)
          })
          .catch(err => reject(err))
      })
    },
    getCreateMaterialDiaryPdfReport ({ rootState }, { startDate, endDate }) {
      return new Promise((resolve, reject) => {
        const businessUnitId = rootState.businessUnits.currentBusinessUnit ? rootState.businessUnits.currentBusinessUnit.id : null
        companies.getCreateMaterialDiaryPdfReport({ businessUnitId, startDate, endDate })
          .then(response => {
            resolve(response)
          })
          .catch(err => reject(err))
      })
    },
    getCreateArticleDiaryPdfReport ({ rootState }, { startDate, endDate, filters }) {
      return new Promise((resolve, reject) => {
        const businessUnitId = rootState.businessUnits.currentBusinessUnit ? rootState.businessUnits.currentBusinessUnit.id : null
        companies.getCreateArticleDiaryPdfReport({ businessUnitId, startDate, endDate, filters })
          .then(response => {
            resolve(response)
          })
          .catch(err => reject(err))
      })
    },
    getArticleDiary ({ rootState }, { startDate, endDate }) {
      return new Promise((resolve, reject) => {
        const businessUnitId = rootState.businessUnits.currentBusinessUnit ? rootState.businessUnits.currentBusinessUnit.id : null
        companies.getArticleDiary({ businessUnitId, startDate, endDate })
          .then(response => {
            resolve(response)
          })
          .catch(err => reject(err))
      })
    },
    loadMyCompany ({ commit }) {
      return new Promise((resolve, reject) => {
        companies.myCompany()
          .then(response => {
            const myCompany = response.data.items[0]
            commit('setMyCompany', myCompany)
            resolve(myCompany)
          })
          .catch(error => {
            notify(error)
            reject(error)
          })
      })
    },
    updateMyCompany ({ commit }, company) {
      return new Promise((resolve, reject) => {
        companies.updateMyCompany({ item: company })
          .then(company => {
            commit('setMyCompany', company)
            resolve(company)
          })
          .catch(error => {
            notify(error)
            reject(error)
          })
      })
    },
    loadChildCompanies ({ commit }) {
      return new Promise((resolve, reject) => {
        companies.childCompanies()
          .then(response => {
            const companies = arrayToObject(response.data.items, 'name')
            commit('setElements', { type: 'childCompanies', elements: companies })
            resolve(companies)
          })
          .catch(error => {
            notify(error)
            reject(error)
          })
      })
    },
    loadChildCompanyUsers ({ state, commit }, childCompanyId) {
      return new Promise((resolve, reject) => {
        companies.childCompanyUsers({ parentCompanyId: state.myCompany.id, childCompanyId })
          .then(response => {
            const users = arrayToObject(response.data.items)
            commit('appendUsersToChildCompany', { companyId: childCompanyId, users })
            resolve(users)
          })
          .catch(error => {
            notify(error)
            reject(error)
          })
      })
    },
    loadChildCompany ({ commit }, id) {
      return new Promise((resolve, reject) => {
        companies.childCompany({ id })
          .then(response => {
            const company = response.data.items[0]
            commit('setElement', { type: 'childCompanies', id: company.id, element: { title: company.name, ...company } })
            resolve(company)
          })
          .catch(error => {
            notify(error)
            reject(error)
          })
      })
    },
    createCompany ({ commit }, company) {
      return new Promise((resolve, reject) => {
        companies.createCompany({ item: company })
          .then(companyId => {
            commit('setElement', { type: 'childCompanies', id: companyId, element: { id: companyId, title: company.company_name, ...company } })
            resolve(company)
          })
          .catch(error => {
            notify(error)
            reject(error)
          })
      })
    },
    updateCompany ({ commit }, company) {
      return new Promise((resolve, reject) => {
        companies.updateCompany({ item: company })
          .then(company => {
            commit('setElement', { type: 'childCompanies', id: company.id, element: company })
            resolve(company)
          })
          .catch(error => {
            notify(error)
            reject(error)
          })
      })
    },
    setCompanyActiveStatus ({ commit, state }, { id, active }) {
      return new Promise((resolve, reject) => {
        companies.setCompanyActiveStatus({ id, status: { active } })
          .then(() => {
            const updatedCompany = { ...state.childCompanies[id], is_active: active }
            commit('setElement', { type: 'childCompanies', id, element: updatedCompany })
            resolve(updatedCompany)
          })
          .catch(error => {
            notify(error)
            reject(error)
          })
      })
    },
    deleteCompany ({ commit }, { id }) {
      commit('removeElement', { type: 'companies', id })
    },
    businessRegister ({ commit }, query) {
      return new Promise((resolve, reject) => {
        companies.businessRegister({ query })
          .then(response => {
            const results = response.data.items
            resolve(results)
          })
          .catch(error => {
            notify(error)
            reject(error)
          })
      })
    },
    getClient (context, { clientId, invoiceId }) {
      return new Promise((resolve, reject) => {
        companies.getClient({ clientId, invoiceId })
          .then(response => {
            const client = response.data.items[0]
            resolve(client)
          })
          .catch(error => reject(error))
      })
    },
    addClient (context, client) {
      return new Promise((resolve, reject) => {
        companies.addClient({ data: client })
          .then(response => {
            const clientId = response.data.items[0].id
            resolve(clientId)
          })
          .catch(error => reject(error))
      })
    },
    takeCompanyIdentity ({ commit, dispatch }, id, allow = true) {
      return new Promise((resolve, reject) => {
        dispatch('auth/tryAutoLogout', {}, { root: true })
          .then(() => {
            companies.takeCompanyIdentity({ id })
              .then(response => {
                const data = response.data.items[0]
                commit('auth/setMaster', null, { root: true })
                commit('auth/setJwt', data.token, { root: true })
                commit('auth/setUserId', data.user_account, { root: true })
                commit('auth/setIdentityState', true, { root: true })
                if (allow) {
                  window.location.href = '/'
                }
              })
              .catch(error => {
                notify(error)
                reject(error)
              })
          })
          .catch(error => reject(error))
      })
    },
    handleInvoiceDuplicate ({ commit }, { id, accept }) {
      return new Promise((resolve, reject) => {
        companies.handleInvoiceDuplicate({ id, accept })
          .then(() => resolve())
          .catch(error => {
            notify(error)
            reject(error)
          })
      })
    },
    vrniCompanyIdentity ({ commit }, id) {
      return new Promise((resolve, reject) => {
        companies.takeCompanyIdentity({ id })
          .then(response => {
            const data = response.data.items[0]
            resolve(data)
          })
          .catch(error => {
            notify(error)
            reject(error)
          })
      })
    },
    invoiceConsistency ({ commit }) {
      return new Promise((resolve, reject) => {
        companies.invoiceConsistency()
          .then(response => {
            const data = response.data.items
            commit('updateInvoiceConsistency', data)
            resolve(data)
          })
          .catch(error => reject(error))
      })
    },
    invoiceDuplicates (context, invoice) {
      return new Promise((resolve, reject) => {
        companies.invoiceDuplicates(invoice)
          .then(response => {
            const data = response.data.items
            resolve(data)
          })
          .catch(error => reject(error))
      })
    },
    getCountries (context) {
      return new Promise((resolve, reject) => {
        companies.getCountries()
          .then(response => {
            const countries = response.data.items
            resolve(countries)
          })
          .catch(error => reject(error))
      })
    }
  },

  mutations: {
    ...mutations,
    setMyCompany (state, company) {
      state.myCompany = company
    },
    appendUsersToChildCompany (state, { companyId, users }) {
      Vue.set(state.childCompanies[companyId], 'users', users)
    },
    updateInvoiceConsistency (state, data) {
      state.invoiceConsistency = data
    }
  }
}
